import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useDarkMode } from "@/hooks/useDarkMode";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, Contact, Package, SlidersHorizontal,
  Wallet, ShoppingCart, Banknote, BarChart3, Moon, Sun, LogOut, Menu, X, BellRing, BadgePercent, CalendarCheck, Target, ScrollText, Landmark, ClipboardList,
} from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/alerts", label: "Alerts", icon: BellRing, badge: true },
  { to: "/dashboard/approvals", label: "Approvals", icon: BadgePercent },
  { to: "/dashboard/field", label: "Field Submissions", icon: ClipboardList },
  { to: "/dashboard/cheques", label: "Cheques (PDC)", icon: Landmark },
  { to: "/dashboard/finance", label: "Finance Close", icon: CalendarCheck, adminOnly: true },
  { to: "/dashboard/targets", label: "Targets & Leaderboard", icon: Target },
  { to: "/dashboard/agents", label: "Agents", icon: Users },
  { to: "/dashboard/customers", label: "Customers", icon: Contact },
  { to: "/dashboard/products", label: "Products & Pricing", icon: Package },
  { to: "/dashboard/orders", label: "Orders / Sales", icon: ShoppingCart },
  { to: "/dashboard/deposits", label: "Deposits & Credit", icon: Wallet },
  { to: "/dashboard/payouts", label: "Payouts", icon: Banknote },
  { to: "/dashboard/policy", label: "Commission Policy & Settings", icon: SlidersHorizontal, adminOnly: true },
  { to: "/dashboard/audit", label: "Audit Log", icon: ScrollText, adminOnly: true },
  { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [dark, setDark] = useDarkMode();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const navItems = useMemo(() => NAV.filter((n) => !n.adminOnly || user?.role === "admin"), [user?.role]);
  useEffect(() => {
    const tick = () => api.get("/admin/alerts").then((r) => setAlertCount(r.data.total)).catch(() => {});
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarInner = () => (
    <>
      <div className="px-5 py-5 border-b border-border/60">
        <Logo size={26} />
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().split(" ")[0]}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <n.icon className="w-[18px] h-[18px]" strokeWidth={1.6} />
            <span className="flex-1">{n.label}</span>
            {n.badge && alertCount > 0 && (
              <span className="min-w-5 h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[11px] font-semibold flex items-center justify-center" data-testid="alerts-badge">{alertCount}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-border/60 space-y-1">
        <div className="px-3 py-2 text-xs text-muted-foreground">
          <div className="font-semibold text-foreground">{user?.name}</div>
          <div className="capitalize">{user?.role}</div>
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-button"
          className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
        >
          <LogOut className="w-[18px] h-[18px]" strokeWidth={1.6} /> Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="w-64 border-r border-border/60 min-h-screen hidden md:flex flex-col fixed inset-y-0 bg-card">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 inset-y-0 w-64 bg-card flex flex-col">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 md:ml-64">
        <header className="h-16 border-b border-border/60 flex items-center justify-between px-4 sm:px-8 sticky top-0 bg-background/80 backdrop-blur-xl z-30">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden p-2"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <span className="text-sm text-muted-foreground hidden sm:block">
              Orgaviya Foods · Company Control Room
            </span>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setDark(!dark)}
            data-testid="theme-toggle"
          >
            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
        </header>
        <main className="p-4 sm:p-8 max-w-[1600px] mx-auto fadeup">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
